import * as React from 'react';
import { Button, Typography } from 'omakase';
import { DateAvailabilityObject } from 'types';
import { IWithContextProps, withContext } from '@tock/coretypes';
import { LocalDate, TemporalAdjusters } from 'js-joda';
import { compareLocalDate } from 'utils/timeUtils';
import { findFocusDate, range } from 'utils/calendar';
import { pluralize } from '@tock/shared/utils/pluralize';
import ConsumerCalendarMonth from 'components/calendar/ConsumerCalendarMonth';
import PureBaseComponent from '@tock/shared/components/PureBaseComponent';
import _ from 'lodash';

interface State {
  displayedMonth: LocalDate;
  focusDate?: LocalDate;
}

interface OwnProps {
  availabilityOverride?: boolean;
  availableDates?: DateAvailabilityObject;
  endDate?: LocalDate;
  initializeToFirstOffered?: boolean;
  labelId?: string;
  lockedDates?: DateAvailabilityObject;
  modifierClassName?: string;
  offeredDates?: DateAvailabilityObject;
  onResetCalendar(): void;
  onSelect(date: string): void;
  onToggleCalendar(e: React.MouseEvent): void;
  openDates: DateAvailabilityObject;
  selectedDates: string[];
  soldDates?: DateAvailabilityObject;
  startDate?: LocalDate;
  wideView?: boolean;
}

type Props = OwnProps & IWithContextProps;
class ConsumerCalendarMultiDaySelect extends PureBaseComponent<Props, State> {
  static defaultProps: Partial<Props>;
  defaultProps = {
    availableDates: {},
    lockedDates: {},
    offeredDates: {},
    openDates: {},
    soldDates: {},
    wideView: true,
  };

  constructor(props: Props) {
    super(props);
    let selectedDate = LocalDate.now();
    const { min } = range(props.offeredDates);
    if (props.selectedDates) {
      selectedDate =
        props.selectedDates.map((date) => LocalDate.parse(date)).sort(compareLocalDate)[0] ||
        LocalDate.now();
    } else if (min && props.initializeToFirstOffered) {
      selectedDate = LocalDate.parse(min);
    }

    this.state = {
      displayedMonth: selectedDate.with(TemporalAdjusters.firstDayOfMonth()),
      focusDate: selectedDate,
    };
  }

  handleKeyDown(event: React.KeyboardEvent<HTMLButtonElement>, date: string) {
    const newDate = findFocusDate(event, date);
    if (newDate) {
      this.setState({
        focusDate: newDate,
      });
      // if the date is the same, then it was selected
      if (newDate.equals(LocalDate.parse(date))) {
        this.handleSelect(date);
      }
    }
  }

  handleSelect(date: string) {
    this.props.onSelect(date);
  }

  handleClickPrev() {
    this.setState({
      displayedMonth: this.state.displayedMonth.minusMonths(1),
    });
  }

  handleClickNext() {
    this.setState({
      displayedMonth: this.state.displayedMonth.plusMonths(1),
    });
  }

  render() {
    const { onToggleCalendar, offeredDates, onResetCalendar, selectedDates } = this.props;
    const { displayedMonth } = this.state;

    let maxLocalDate;
    let minLocalDate;
    let nextDisabled;
    let prevDisabled;
    if (offeredDates) {
      const { min, max } = range(offeredDates);
      minLocalDate = min ? LocalDate.parse(min) : LocalDate.now();
      maxLocalDate = max ? LocalDate.parse(max) : LocalDate.now();
      nextDisabled = !max || displayedMonth.plusMonths(1).isAfter(maxLocalDate);
      prevDisabled = !min || displayedMonth.minusMonths(1).isBefore(minLocalDate.minusMonths(1));
    }

    if (_.isEmpty(offeredDates)) {
      minLocalDate = this.props.startDate;
      maxLocalDate = this.props.endDate;
      if (maxLocalDate && minLocalDate) {
        nextDisabled = displayedMonth.plusMonths(1).isAfter(maxLocalDate);
        prevDisabled = displayedMonth.minusMonths(1).isBefore(minLocalDate.minusMonths(1));
      }
    }

    return (
      <>
        <div className="Consumer-waitlistDropdownHeader" aria-labelledby="selected-dates">
          <Typography color="inherit" component="span" variant="body02Strong">
            Choose days to join the Notify list
          </Typography>
          <Typography id="selected-dates" component="span" color="inherit" variant="body02Strong">
            <span className="Consumer-waitlistDropdownHeaderInfo">
              {pluralize(selectedDates.length, 'day')}
            </span>
            <button
              className="Consumer-waitlistDropdownHeaderLink"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onResetCalendar();
                return false;
              }}
            >
              <Typography color="inherit" component="span" variant="body02">
                Reset
              </Typography>
            </button>
          </Typography>
        </div>
        <ConsumerCalendarMonth
          {...this.props}
          disableNext={nextDisabled}
          disablePrev={prevDisabled}
          focusDate={this.state.focusDate}
          handleKeyDown={this.handleKeyDown}
          month={displayedMonth}
          onClickNext={this.handleClickNext}
          onClickPrev={this.handleClickPrev}
          onSelectDate={this.handleSelect}
        />
        <div className="Consumer-waitlistDropdownFooter">
          <Button onClick={onToggleCalendar} variant="primary" size="medium">
            Done
          </Button>
        </div>
      </>
    );
  }
}

export default withContext(ConsumerCalendarMultiDaySelect);
